import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Gauge, Clock, FileWarning, CalendarClock, FileCheck2, Inbox,
  Loader2, Settings2, TrendingUp, Star,
} from 'lucide-react';

const RAG = {
  green: { label: 'On track', dot: 'bg-emerald-500', text: 'text-emerald-700', ring: 'ring-emerald-200', bg: 'bg-emerald-50', bar: 'bg-emerald-500' },
  amber: { label: 'At risk', dot: 'bg-amber-500', text: 'text-amber-700', ring: 'ring-amber-200', bg: 'bg-amber-50', bar: 'bg-amber-500' },
  red: { label: 'Breached', dot: 'bg-rose-500', text: 'text-rose-700', ring: 'ring-rose-200', bg: 'bg-rose-50', bar: 'bg-rose-500' },
};
const ICONS = {
  issue_to_arrived: Clock,
  arrived_to_processed: FileCheck2,
  missing_bills: Inbox,
  overdue_bills: CalendarClock,
  pf_approvals: FileWarning,
};
const hoursLabel = (h) => (h >= 72 ? `${h / 24}d` : `${h}h`);

export default function KpiDashboard() {
  const navigate = useNavigate();
  const acct = getCurrentAccount();
  const espmEnabled = acct?.enabled_apps?.energy_star === true;
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [espm, setEspm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState('30');

  const load = useCallback(async () => {
    if (!acct?.id) return;
    setLoading(true);
    try {
      const calls = [
        api.get('/dashboard/org/mongo/intake-kpi/dashboard', { params: { account_id: acct.id, window_days: windowDays } }),
        api.get('/dashboard/org/mongo/intake-kpi/trend', { params: { account_id: acct.id, days: 30 } }),
      ];
      if (espmEnabled) calls.push(api.get('/energy-star/report', { params: { account_id: acct.id } }));
      const res = await Promise.allSettled(calls);
      if (res[0].status === 'fulfilled') setData(res[0].value.data);
      if (res[1].status === 'fulfilled') setTrend(res[1].value.data?.snapshots || []);
      if (espmEnabled && res[2]?.status === 'fulfilled') setEspm(res[2].value.data?.summary || null);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [acct?.id, windowDays, espmEnabled]);

  useEffect(() => { load(); }, [load]);

  const overall = data?.overall_status || 'green';

  return (
    <MainLayout>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="kpi-dashboard-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Gauge className="h-7 w-7 text-primary" />
            <div>
              <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>KPI Dashboard</h1>
              <p className="text-sm text-muted-foreground">Operational SLA health across the bill stream</p>
            </div>
            {data && (
              <span className={`ml-3 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${RAG[overall].bg} ${RAG[overall].text}`} data-testid="kpi-overall-status">
                <span className={`h-2 w-2 rounded-full ${RAG[overall].dot}`} /> {RAG[overall].label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={windowDays} onValueChange={setWindowDays}>
              <SelectTrigger className="w-36 rounded-none h-9 text-xs" data-testid="kpi-window-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-none h-9" onClick={() => navigate('/settings?tab=kpi')} data-testid="kpi-configure-btn">
              <Settings2 className="h-4 w-4 mr-2" /> Configure KPIs
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading KPIs…
          </div>
        ) : !data ? (
          <div className="text-center py-24 text-muted-foreground">Unable to load KPIs.</div>
        ) : (
          <>
            {espmEnabled && espm && (
              <button
                onClick={() => navigate('/bills/energy-star')}
                className="w-full text-left border border-border bg-card p-5 mb-5 hover:ring-1 hover:ring-amber-200 transition-all"
                data-testid="kpi-espm-card"
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <Star className="h-6 w-6 text-amber-500" />
                    <div>
                      <div className="text-sm font-bold">ENERGY STAR Reporting</div>
                      <div className="text-xs text-muted-foreground">
                        {espm.metrics_available}/{espm.properties_mapped} properties scored · {espm.environment === 'test' ? 'Sandbox' : 'Production'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <EspmStat label="Avg score" value={espm.avg_score ?? '—'}
                      tone={espm.avg_score == null ? '' : espm.avg_score >= 75 ? 'text-emerald-700' : espm.avg_score >= 50 ? 'text-amber-700' : 'text-rose-700'} />
                    <EspmStat label="Certifiable" value={espm.certifiable ?? 0} />
                    <EspmStat label="Months loaded" value={`${espm.completeness_pct ?? 0}%`}
                      tone={espm.status === 'green' ? 'text-emerald-700' : espm.status === 'amber' ? 'text-amber-700' : 'text-rose-700'} />
                    <EspmStat label="Pending" value={espm.metrics_pending ?? 0} />
                    <span className="text-xs text-primary font-medium whitespace-nowrap">View report →</span>
                  </div>
                </div>
              </button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="kpi-cards-grid">
              {data.kpis.map((k) => <KpiCard key={k.key} kpi={k} trend={trend} />)}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}

function EspmStat({ label, value, tone = '' }) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-black tabular-nums ${tone}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function KpiCard({ kpi, trend }) {
  const rag = RAG[kpi.status] || RAG.green;
  const Icon = ICONS[kpi.key] || Gauge;
  const { green, amber, red } = kpi.counts;
  const total = kpi.counts.total || 0;
  const pct = (n) => (total ? `${(n / total) * 100}%` : '0%');
  const spark = trend.map((s) => (s.kpis || []).find((x) => x.key === kpi.key)?.avg_days ?? 0);
  const sparkMax = Math.max(1, ...spark);

  return (
    <div className={`border border-border bg-card p-5 ring-1 ${rag.ring}`} data-testid={`kpi-card-${kpi.key}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${rag.text}`} />
          <div className="text-sm font-bold">{kpi.label}</div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${rag.bg} ${rag.text}`} data-testid={`kpi-status-${kpi.key}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${rag.dot}`} /> {rag.label}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{kpi.description}</p>

      <div className="flex items-end gap-2 mt-3">
        <div className="text-4xl font-black tabular-nums" data-testid={`kpi-avg-${kpi.key}`}>{kpi.has_data ? kpi.avg_days : '—'}</div>
        <div className="text-xs text-muted-foreground mb-1.5">avg days{kpi.has_data ? ` · max ${kpi.max_days}d` : ''}</div>
      </div>

      {/* RAG distribution bar */}
      <div className="mt-3">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" data-testid={`kpi-bar-${kpi.key}`}>
          <div className="bg-emerald-500" style={{ width: pct(green) }} />
          <div className="bg-amber-500" style={{ width: pct(amber) }} />
          <div className="bg-rose-500" style={{ width: pct(red) }} />
        </div>
        <div className="flex items-center justify-between text-[11px] mt-1.5">
          <span className="text-emerald-700 font-semibold">{green} green</span>
          <span className="text-amber-700 font-semibold">{amber} amber</span>
          <span className="text-rose-700 font-semibold">{red} red</span>
        </div>
      </div>

      {/* thresholds + sparkline */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
        <div className="text-[11px] text-muted-foreground">
          SLA: <span className="text-emerald-700 font-semibold">≤{hoursLabel(kpi.thresholds.green)}</span> ·{' '}
          <span className="text-amber-700 font-semibold">≤{hoursLabel(kpi.thresholds.amber)}</span> ·{' '}
          <span className="text-rose-700 font-semibold">&gt;{hoursLabel(kpi.thresholds.amber)}</span>
        </div>
        {spark.length > 1 && (
          <div className="flex items-end gap-0.5 h-6" title="30-day trend (avg days)">
            {spark.slice(-14).map((v, i) => (
              <div key={i} className={`w-1 ${rag.bar} opacity-70`} style={{ height: `${Math.max(6, (v / sparkMax) * 100)}%` }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
